/**
 * Product settings: merchant of record, seller and terms (TMMPDM-0092), standing notice CC, card
 * processing fees per currency (TMMPDM-0089), billing maintenance (TMMPDM-0094) and the other settings
 * (read-only). TMMPDM-0095: the ledger mode is read-only on Ledger cut-over.
 * TMMPDM-0100: a side menu, one section at a time (`?tab=`).
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { oneOf, useUrlState, type UrlSchema } from '@/hooks/useUrlState';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  LocalNav,
  PageHeader,
  SectionCard,
  SubmitButton,
  useNotify,
} from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { ApiError, errorCopy, isValidationFailed, validationFields } from '@/api/errors';
import type { LedgerMode, ProductSettingView, TenantView } from '@/api/tmm/types';
import { ledgerModeOf } from './ledger/ledger';
import { formatInstant } from '@/format';
import { BillingMaintenanceSection } from './billing/BillingMaintenanceSection';
import { LedgerCutoverSection } from './billing/LedgerCutoverSection';
import { ProcessingFeesSection } from './billing/ProcessingFeesSection';

const SETTINGS_TABS = [
  'merchant',
  'seller',
  'standingCc',
  'fees',
  'maintenance',
  'ledgerCutover',
  'other',
] as const;
type SettingsTab = (typeof SETTINGS_TABS)[number];
type SettingsView = { tab: SettingsTab };
const SETTINGS_VIEW: UrlSchema<SettingsView> = {
  tab: { default: 'merchant', parse: oneOf(SETTINGS_TABS) },
};

const MERCHANT_KEY = 'billing.merchant_tenant_slug';
const STANDING_CC_KEY = 'notifications.reseller_standing_cc';
const SEARCH_DEBOUNCE_MS = 250;
/** tmm-api's own limit and address shape (ProductSettingsService, 0100). */
const STANDING_CC_MAX = 255;
const EMAIL = /^[^@\s,;]+@[^@\s,;]+\.[^@\s,;]+$/;
/** The seller and terms settings (tmm-api 0156 U1), in the order the section shows them. */
const SELLER_KEYS = [
  'seller_name',
  'seller_legal_name',
  'consumer_terms_version',
  'consumer_terms_url',
  'privacy_policy_url',
] as const;
type SellerKey = (typeof SELLER_KEYS)[number];
const SELLER_KEY_SET: ReadonlySet<string> = new Set(SELLER_KEYS);
const URL_KEYS: ReadonlySet<SellerKey> = new Set(['consumer_terms_url', 'privacy_policy_url']);
/** tmm-api's rules (ProductSettingsService, 0156 U1): a token, not a number; every value ≤ 255. */
const TERMS_VERSION = /^[A-Za-z0-9._-]{1,32}$/;
const SETTING_MAX = 255;
const TERMS_VERSION_KEY: SellerKey = 'consumer_terms_version';

export function ProductSettings() {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const [view] = useUrlState(SETTINGS_VIEW);
  const [reloadToken, setReloadToken] = useState(0);
  const [settings, setSettings] = useState<ProductSettingView[] | null>(null);
  const [ledgerMode, setLedgerMode] = useState<LedgerMode | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [merchantTenant, setMerchantTenant] = useState<TenantView | null>(null);
  const [changeOpen, setChangeOpen] = useState(false);
  // The change dialog stays open on a failed save, so its error is said inside it, once.
  const [merchantError, setMerchantError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => setReloadToken((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .getProductSettings()
      .then(async (view) => {
        if (cancelled) return;
        setSettings(view.settings);
        setLedgerMode(ledgerModeOf(view.ledgerMode));
        setError(null);
        const merchant = view.settings.find((s) => s.key === MERCHANT_KEY);
        if (merchant?.value) {
          try {
            const tenant = await adminApi.getTenant(merchant.value);
            if (!cancelled) setMerchantTenant(tenant);
          } catch {
            if (!cancelled) setMerchantTenant(null);
          }
        } else if (!cancelled) {
          setMerchantTenant(null);
        }
      })
      .catch((failure: unknown) => {
        if (!cancelled) {
          setSettings(null);
          setError(failure ?? 'load-failed');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const merchantSetting = settings?.find((s) => s.key === MERCHANT_KEY);
  const standingCcSetting = settings?.find((s) => s.key === STANDING_CC_KEY);
  const sellerSettings = (settings ?? []).filter((s) => SELLER_KEY_SET.has(s.key));
  const otherSettings = (settings ?? []).filter(
    (s) => s.key !== MERCHANT_KEY && s.key !== STANDING_CC_KEY && !SELLER_KEY_SET.has(s.key),
  );

  const saveMerchant = async (slug: string) => {
    setBusy(true);
    setMerchantError(null);
    try {
      await adminApi.putProductSettings({ [MERCHANT_KEY]: slug });
      setChangeOpen(false);
      load();
    } catch (err) {
      // The dialog has no Reload, so the shared "Reload and try again" would point at nothing here.
      setMerchantError(
        err instanceof ApiError && String(err.code) === 'CONCURRENT_UPDATE'
          ? t(
              'settings.merchant.concurrent',
              'Someone else changed this at the same time. Try again.',
            )
          : errorCopy(err, t, t('settings.merchant.saveError', 'Could not save the setting.')),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title={t('page.settings.title', 'Product settings')}
        subtitle={t('page.settings.subtitle', 'Platform-wide settings for this product.')}
      />

      {error ? (
        <Box sx={{ mb: 2 }}>
          <ErrorState
            error={error}
            message={t('settings.loadError', 'Could not load product settings.')}
            onRetry={load}
          />
        </Box>
      ) : null}

      <LocalNav
        ariaLabel={t('page.settings.title', 'Product settings')}
        activeKey={view.tab}
        groups={[
          {
            key: 'commerce',
            label: t('settings.nav.commerce', 'Commerce'),
            items: [
              {
                key: 'merchant',
                label: t('settings.merchant.title', 'Merchant of record'),
                to: pathname,
              },
              {
                key: 'seller',
                label: t('settings.seller.title', 'Seller and terms'),
                to: '?tab=seller',
              },
              {
                key: 'standingCc',
                label: t('settings.nav.standingCc', 'Standing CC'),
                to: '?tab=standingCc',
              },
              {
                key: 'fees',
                label: t('settings.nav.fees', 'Processing fees'),
                to: '?tab=fees',
              },
            ],
          },
          {
            key: 'operations',
            label: t('settings.nav.operations', 'Operations'),
            items: [
              {
                key: 'maintenance',
                label: t('settings.maintenance.title', 'Billing maintenance'),
                to: '?tab=maintenance',
              },
              {
                key: 'ledgerCutover',
                label: t('ledger.cutover.title', 'Ledger cut-over'),
                to: '?tab=ledgerCutover',
              },
            ],
          },
          {
            key: 'other',
            label: t('settings.nav.other', 'Other'),
            items: [
              {
                key: 'other',
                label: t('settings.other.title', 'Other settings'),
                to: '?tab=other',
              },
            ],
          },
        ]}
      >
        {view.tab === 'merchant' ? (
          <SectionCard
            title={t('settings.merchant.title', 'Merchant of record')}
            subtitle={t(
              'settings.merchant.help',
              'The tenant whose payment account charges tenants for add-ons.',
            )}
          >
            {merchantSetting?.value ? (
              <Typography variant="body1" sx={{ mb: 1 }}>
                {merchantTenant ? (
                  <>
                    {merchantTenant.name} (
                    <Box component="span" sx={(theme) => theme.typography.mono}>
                      {merchantTenant.slug}
                    </Box>
                    )
                  </>
                ) : (
                  // The tenant couldn't be read: the setting's value is the slug itself.
                  <Box component="span" sx={(theme) => theme.typography.mono}>
                    {merchantSetting.value}
                  </Box>
                )}
              </Typography>
            ) : (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {t(
                  'settings.merchant.unset',
                  'Not set. Add-on card charges are paused until this is set.',
                )}
              </Alert>
            )}
            {merchantSetting?.updatedAt ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {t('settings.updatedAt', 'Updated {{when}}', {
                  when: formatInstant(merchantSetting.updatedAt, i18n.language),
                })}
              </Typography>
            ) : null}
            <Button
              variant="outlined"
              onClick={() => {
                setMerchantError(null);
                setChangeOpen(true);
              }}
            >
              {t('settings.merchant.change', 'Change…')}
            </Button>
          </SectionCard>
        ) : null}

        {view.tab === 'seller' && settings ? (
          <SellerTermsSection
            key={sellerSettings.map((s) => `${s.key}=${s.value ?? ''}`).join('\n')}
            settings={sellerSettings}
            onSaved={load}
          />
        ) : null}

        {view.tab === 'standingCc' && settings ? (
          <StandingCcSection
            key={standingCcSetting?.value ?? ''}
            setting={standingCcSetting}
            onSaved={load}
          />
        ) : null}

        {view.tab === 'fees' ? <ProcessingFeesSection /> : null}

        {view.tab === 'maintenance' ? <BillingMaintenanceSection /> : null}

        {view.tab === 'ledgerCutover' ? <LedgerCutoverSection mode={ledgerMode} /> : null}

        {view.tab === 'other' ? (
          <SectionCard title={t('settings.other.title', 'Other settings')}>
            {settings !== null && otherSettings.length === 0 ? (
              <EmptyState title={t('settings.other.empty', 'No other settings.')} />
            ) : settings !== null ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small" aria-label={t('settings.other.title', 'Other settings')}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('settings.col.key', 'Key')}</TableCell>
                      <TableCell>{t('settings.col.value', 'Value')}</TableCell>
                      <TableCell>{t('settings.col.updated', 'Updated')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {otherSettings.map((row) => (
                      <TableRow key={row.key}>
                        <TableCell>{row.key}</TableCell>
                        <TableCell>{row.value ?? '—'}</TableCell>
                        <TableCell>{formatInstant(row.updatedAt, i18n.language)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : null}
          </SectionCard>
        ) : null}
      </LocalNav>

      <ChangeMerchantDialog
        open={changeOpen}
        onClose={() => setChangeOpen(false)}
        onSave={(slug) => void saveMerchant(slug)}
        busy={busy}
        error={merchantError}
      />
    </Box>
  );
}

function ChangeMerchantDialog({
  open,
  onClose,
  onSave,
  busy,
  error,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (slug: string) => void;
  busy: boolean;
  error: string | null;
}) {
  const { t } = useCopy();
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<TenantView[]>([]);
  const [selected, setSelected] = useState<TenantView | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => setQuery(input.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [input, open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    adminApi
      .listTenants({ page: 0, size: 20, ...(query ? { q: query } : {}) })
      .then((page) => {
        if (!cancelled) setOptions(page.tenants);
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [query, open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('settings.merchant.changeTitle', 'Change merchant of record')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <Autocomplete
            options={options}
            loading={loading}
            value={selected}
            onChange={(_e, value) => setSelected(value)}
            inputValue={input}
            onInputChange={(_e, value) => setInput(value)}
            filterOptions={(x) => x}
            getOptionLabel={(option) => `${option.name} (${option.slug})`}
            // The input shows plain text; in the list the slug is an identifier, so it is mono.
            renderOption={(props, option) => {
              const { key, ...rest } = props;
              return (
                <li key={key} {...rest}>
                  {option.name} (
                  <Box component="span" sx={(theme) => theme.typography.mono}>
                    {option.slug}
                  </Box>
                  )
                </li>
              );
            }}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField {...params} label={t('settings.merchant.tenantLabel', 'Tenant')} />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('resellers.cancel', 'Cancel')}
        </Button>
        <SubmitButton
          busy={busy}
          disabled={!selected}
          onClick={() => selected && onSave(selected.slug)}
        >
          {t('tenants.edit.save', 'Save')}
        </SubmitButton>
      </DialogActions>
    </Dialog>
  );
}

function standingCcError(
  value: string,
  t: (key: string, fallback?: string, values?: Record<string, string | number>) => string,
): string | null {
  const entries: string[] = [];
  for (const part of value.split(',')) {
    const entry = part.trim();
    if (entry === '') continue;
    if (!EMAIL.test(entry)) {
      return t('settings.standingCc.invalid', '“{{entry}}” isn’t an email address.', { entry });
    }
    if (!entries.some((known) => known.toLowerCase() === entry.toLowerCase())) entries.push(entry);
  }
  if (entries.join(',').length > STANDING_CC_MAX) {
    return t('settings.standingCc.tooLong', 'At most {{max}} characters in all.', {
      max: STANDING_CC_MAX,
    });
  }
  return null;
}

/** The standing notice CC (TMMPDM-0038): one field, its own Save. Keyed by the saved value. */
function StandingCcSection({
  setting,
  onSaved,
}: {
  setting: ProductSettingView | undefined;
  onSaved: () => void;
}) {
  const { t } = useCopy();
  const notify = useNotify();
  const { i18n } = useTranslation();
  const [value, setValue] = useState(setting?.value ?? '');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const title = t('settings.standingCc.title', 'Standing notice CC');

  const submit = async () => {
    const problem = standingCcError(value, t);
    setFieldError(problem);
    if (problem !== null) return;
    setBusy(true);
    try {
      await adminApi.putProductSettings({ [STANDING_CC_KEY]: value.trim() });
      notify.success(t('settings.standingCc.saved', 'Standing notice CC saved.'));
      onSaved();
    } catch (err) {
      const onField = isValidationFailed(err)
        ? validationFields(err, t)[STANDING_CC_KEY]
        : undefined;
      setFieldError(
        onField ??
          errorCopy(err, t, t('settings.merchant.saveError', 'Could not save the setting.')),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionCard title={title} id="settings-standing-cc-title">
      <Box
        component="form"
        noValidate
        aria-labelledby="settings-standing-cc-title"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ alignItems: 'flex-start' }}
        >
          <TextField
            label={title}
            value={value}
            onChange={(event) => {
              setValue(event.target.value);
              setFieldError(null);
            }}
            error={fieldError !== null}
            helperText={
              fieldError ??
              t(
                'settings.standingCc.helper',
                'Comma-separated email addresses copied on every standing notice',
              )
            }
            sx={{ flex: 1, maxWidth: 640, width: '100%' }}
          />
          <SubmitButton type="submit" busy={busy} variant="outlined" sx={{ mt: 1 }}>
            {t('settings.standingCc.save', 'Save CC')}
          </SubmitButton>
        </Stack>
        {setting?.value ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {t('settings.updatedAt', 'Updated {{when}}', {
              when: formatInstant(setting.updatedAt, i18n.language),
            })}
          </Typography>
        ) : null}
      </Box>
    </SectionCard>
  );
}

type SellerValues = Record<SellerKey, string>;
type SellerErrors = Partial<Record<SellerKey, string>>;
type Translate = (
  key: string,
  fallback?: string,
  values?: Record<string, string | number>,
) => string;

function isHttpsUrl(value: string): boolean {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

function versionRule(t: Translate): string {
  return t(
    'settings.seller.versionInvalid',
    'Use only letters, digits, dot, underscore or dash; up to 32.',
  );
}

function urlRule(t: Translate): string {
  return t(
    'settings.seller.urlInvalid',
    'Enter an https address of at most {{max, number}} characters, or leave it empty.',
    { max: SETTING_MAX },
  );
}

/** The client's copy of tmm-api's 0156 U1 rules: required, the version token, https URLs. */
function sellerErrors(values: SellerValues, t: Translate): SellerErrors {
  const required: Partial<Record<SellerKey, string>> = {
    seller_name: t('settings.seller.nameRequired', 'Seller name is required.'),
    seller_legal_name: t('settings.seller.legalNameRequired', 'Legal name is required.'),
    consumer_terms_version: t('settings.seller.versionRequired', 'Terms version is required.'),
  };
  const errors: SellerErrors = {};
  for (const key of SELLER_KEYS) {
    const value = values[key];
    const requiredCopy = required[key];
    if (value === '') {
      if (requiredCopy !== undefined) errors[key] = requiredCopy;
    } else if (key === TERMS_VERSION_KEY) {
      if (!TERMS_VERSION.test(value)) errors[key] = versionRule(t);
    } else if (URL_KEYS.has(key)) {
      if (value.length > SETTING_MAX || !isHttpsUrl(value)) errors[key] = urlRule(t);
    } else if (value.length > SETTING_MAX) {
      errors[key] = t('settings.seller.tooLong', 'At most {{max, number}} characters.', {
        max: SETTING_MAX,
      });
    }
  }
  return errors;
}

/**
 * Seller and terms (TMMPDM-0092, tmm-api 0156 U1): the five consumer billing identity settings and
 * one Save that sends only the changed keys. A new terms version is confirmed first: new
 * subscribers must accept it. Keyed by the saved values, so a reload resets it.
 */
function SellerTermsSection({
  settings,
  onSaved,
}: {
  settings: ProductSettingView[];
  onSaved: () => void;
}) {
  const { t } = useCopy();
  const notify = useNotify();
  const { i18n } = useTranslation();
  const saved = Object.fromEntries(
    SELLER_KEYS.map((key) => [key, settings.find((s) => s.key === key)?.value ?? '']),
  ) as SellerValues;
  const [values, setValues] = useState<SellerValues>(saved);
  const [errors, setErrors] = useState<SellerErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  // The changes waiting on the version confirm; null while it is closed.
  const [confirming, setConfirming] = useState<Record<string, string> | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const lastUpdated = settings.reduce<string | null>(
    (latest, s) =>
      s.updatedAt && (latest === null || s.updatedAt > latest) ? s.updatedAt : latest,
    null,
  );

  const labels: Record<SellerKey, string> = {
    seller_name: t('settings.seller.name', 'Seller name'),
    seller_legal_name: t('settings.seller.legalName', 'Legal name'),
    consumer_terms_version: t('settings.seller.version', 'Terms version'),
    consumer_terms_url: t('settings.seller.termsUrl', 'Terms URL'),
    privacy_policy_url: t('settings.seller.privacyUrl', 'Privacy policy URL'),
  };
  const urlHint = t('settings.seller.urlHint', 'Leave empty to show none.');
  const hints: Partial<Record<SellerKey, string>> = {
    consumer_terms_version: t(
      'settings.seller.versionHint',
      'Letters, digits, dot, underscore or dash; up to 32. For example 2026-10.',
    ),
    consumer_terms_url: urlHint,
    privacy_policy_url: urlHint,
  };

  const save = async (changes: Record<string, string>, fromConfirm: boolean) => {
    setBusy(true);
    setFormError(null);
    setConfirmError(null);
    try {
      await adminApi.putProductSettings(changes);
      setConfirming(null);
      notify.success(t('settings.seller.saved', 'Seller and terms saved.'));
      onSaved();
    } catch (err) {
      // TMM-B3: a 400's fields get this form's own rule copy, never the server's text.
      const onFields = isValidationFailed(err)
        ? validationFields(err, t, {
            consumer_terms_version: versionRule(t),
            consumer_terms_url: urlRule(t),
            privacy_policy_url: urlRule(t),
          })
        : {};
      const mine: SellerErrors = {};
      for (const key of SELLER_KEYS) {
        const copy = onFields[key];
        if (copy !== undefined) mine[key] = copy;
      }
      const fallback = errorCopy(
        err,
        t,
        t('settings.merchant.saveError', 'Could not save the setting.'),
      );
      if (Object.keys(mine).length > 0) {
        // The fix is on the fields, so the confirm closes and the fields say what to change.
        setConfirming(null);
        setErrors(mine);
      } else if (fromConfirm) {
        // TMM-B4: the confirm is open, so its failure is said inside it.
        setConfirmError(fallback);
      } else {
        setFormError(fallback);
      }
    } finally {
      setBusy(false);
    }
  };

  const submit = () => {
    const trimmed = Object.fromEntries(
      SELLER_KEYS.map((key) => [key, values[key].trim()]),
    ) as SellerValues;
    const problems = sellerErrors(trimmed, t);
    setErrors(problems);
    setFormError(null);
    if (Object.keys(problems).length > 0) return;
    // Only the changed keys; an emptied URL is sent as "" (tmm-api serves it as null).
    const changes: Record<string, string> = {};
    for (const key of SELLER_KEYS) {
      if (trimmed[key] !== saved[key]) changes[key] = trimmed[key];
    }
    if (Object.keys(changes).length === 0) {
      notify.info(t('settings.seller.unchanged', 'Nothing changed, so nothing was saved.'));
      return;
    }
    if (TERMS_VERSION_KEY in changes) {
      setConfirmError(null);
      setConfirming(changes);
      return;
    }
    void save(changes, false);
  };

  const newVersion = confirming?.[TERMS_VERSION_KEY] ?? '';

  return (
    <SectionCard title={t('settings.seller.title', 'Seller and terms')} id="settings-seller-title">
      <Box
        component="form"
        noValidate
        aria-labelledby="settings-seller-title"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <Stack spacing={2} sx={{ maxWidth: 640 }}>
          {formError ? <Alert severity="error">{formError}</Alert> : null}
          {SELLER_KEYS.map((key) => (
            <TextField
              key={key}
              label={labels[key]}
              value={values[key]}
              required={!URL_KEYS.has(key)}
              onChange={(event) => {
                const next = event.target.value;
                setValues((current) => ({ ...current, [key]: next }));
                setErrors((current) => {
                  const rest = { ...current };
                  delete rest[key];
                  return rest;
                });
              }}
              error={errors[key] !== undefined}
              helperText={errors[key] ?? hints[key]}
              {...(URL_KEYS.has(key) ? { slotProps: { htmlInput: { inputMode: 'url' } } } : {})}
            />
          ))}
          <Box>
            <SubmitButton type="submit" busy={busy && confirming === null} variant="outlined">
              {t('tenants.edit.save', 'Save')}
            </SubmitButton>
          </Box>
        </Stack>
        {lastUpdated ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {t('settings.updatedAt', 'Updated {{when}}', {
              when: formatInstant(lastUpdated, i18n.language),
            })}
          </Typography>
        ) : null}
        <ConfirmDialog
          open={confirming !== null}
          title={t(
            'settings.seller.versionConfirm.title',
            'Change the terms version to {{termsVersion}}?',
            { termsVersion: newVersion },
          )}
          consequence={t(
            'settings.seller.versionConfirm.body',
            "New subscribers must accept version {{termsVersion}} before subscribing. Existing subscribers aren't forced to re-accept; the app may ask them. Their subscriptions continue.",
            { termsVersion: newVersion },
          )}
          confirmLabel={t('settings.seller.versionConfirm.confirm', 'Change version')}
          busy={busy}
          error={confirmError}
          onConfirm={() => {
            if (confirming) void save(confirming, true);
          }}
          onCancel={() => setConfirming(null)}
        />
      </Box>
    </SectionCard>
  );
}
